import { useMemo } from 'react';
import type { LessonLog } from '@/types';

export interface MonthCursor {
  year: number;
  month: number; // 0-11
}

interface LessonCalendarProps {
  lessons: LessonLog[];
  cursor: MonthCursor;
  onCursorChange: (next: MonthCursor) => void;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
}

const WEEK_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

/** lesson_date(Y-m-d 또는 ISO)를 YYYY-MM-DD 키로 정규화해요. */
export function lessonDateKey(value: string): string {
  const match = /^\d{4}-\d{2}-\d{2}/.exec(value);
  if (match) return match[0];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

export default function LessonCalendar({
  lessons,
  cursor,
  onCursorChange,
  selectedDate,
  onSelectDate,
}: LessonCalendarProps) {
  const byDate = useMemo(() => {
    const map: Record<string, LessonLog[]> = {};
    lessons.forEach((l) => {
      const key = lessonDateKey(l.lesson_date);
      if (!map[key]) map[key] = [];
      map[key].push(l);
    });
    return map;
  }, [lessons]);

  const todayKey = useMemo(() => lessonDateKey(new Date().toISOString()), []);

  const { year, month } = cursor;
  const monthPrefix = `${year}-${pad(month + 1)}`;

  const startWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < startWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLessonCount = lessons.filter((l) =>
    lessonDateKey(l.lesson_date).startsWith(monthPrefix),
  ).length;

  const goMonth = (delta: number) => {
    const d = new Date(year, month + delta, 1);
    onCursorChange({ year: d.getFullYear(), month: d.getMonth() });
  };

  const goToday = () => {
    const now = new Date();
    onCursorChange({ year: now.getFullYear(), month: now.getMonth() });
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-100 p-3 md:p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => goMonth(-1)}
            aria-label="이전 달"
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-600 transition hover:bg-background-200 hover:text-primary-700"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <p className="min-w-[92px] text-center text-sm font-bold text-foreground-950">
            {year}년 {month + 1}월
          </p>
          <button
            type="button"
            onClick={() => goMonth(1)}
            aria-label="다음 달"
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-600 transition hover:bg-background-200 hover:text-primary-700"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>

        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-xs text-foreground-500">
            <i className="ri-book-2-line text-primary-600"></i>
            이 달{' '}
            <strong className="font-semibold text-primary-700">{monthLessonCount}</strong>회 수업
          </span>
          <button
            type="button"
            onClick={goToday}
            className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1 text-xs font-medium text-foreground-600 transition hover:border-primary-300 hover:text-primary-700 whitespace-nowrap"
          >
            오늘
          </button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-7 gap-1 md:gap-1.5">
        {WEEK_LABELS.map((label, i) => (
          <div
            key={label}
            className={`pb-1 text-center text-xs font-medium ${
              i === 0 ? 'text-accent-600' : i === 6 ? 'text-foreground-500' : 'text-foreground-500'
            }`}
          >
            {label}
          </div>
        ))}

        {cells.map((day, idx) => {
          if (day === null) {
            return <div key={`empty-${idx}`} className="h-11 md:h-12" />;
          }
          const dateKey = `${monthPrefix}-${pad(day)}`;
          const dayLessons = byDate[dateKey] ?? [];
          const hasLesson = dayLessons.length > 0;
          const isSelected = selectedDate === dateKey;
          const isToday = todayKey === dateKey;
          const hasAttend = dayLessons.some((l) => !l.is_absent);

          let cellClass =
            'flex h-11 md:h-12 flex-col items-center justify-center rounded-lg text-sm font-medium transition ';
          if (isSelected) {
            cellClass += 'bg-primary-500 text-background-50';
          } else if (hasLesson) {
            cellClass += 'bg-primary-100 text-primary-800 hover:bg-primary-200';
          } else {
            cellClass += 'text-foreground-700 hover:bg-background-200';
          }
          if (isToday && !isSelected) {
            cellClass += ' ring-1 ring-primary-400';
          }

          return (
            <button
              key={dateKey}
              type="button"
              onClick={() => onSelectDate(dateKey)}
              aria-label={`${month + 1}월 ${day}일${hasLesson ? ` 수업 ${dayLessons.length}건` : ''}`}
              className={cellClass}
            >
              <span className="leading-none">{day}</span>
              {isSelected ? (
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-background-50/80" />
              ) : hasLesson ? (
                <span
                  className={`mt-1 h-1.5 w-1.5 rounded-full ${
                    hasAttend ? 'bg-primary-600' : 'bg-accent-500'
                  }`}
                />
              ) : (
                <span className="mt-1 h-1.5 w-1.5" />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-background-200 pt-3 text-xs text-foreground-500">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-primary-600"></span>
          수업한 날
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-accent-500"></span>
          결석한 날
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-background-50 ring-1 ring-primary-400"></span>
          오늘
        </span>
      </div>
    </div>
  );
}